import { Suspense } from "react";

import { PageSkeleton } from "@stacklet/ui";
import { lazily } from "react-lazily";

import { lazyRouteLoader } from "app/utils/lazyRouteLoader";

import type { PreloadingRouteProps } from "app/utils/lazyRouteLoader";

// Routes.tsx imports this module statically, so it is part of the app shell's
// bundle: the page and its preload module may only be imported dynamically
// here, or the split that lets the loader run ahead of the page's chunk is
// lost.

const { BindingDetailsRoute } = lazily(() => import("./BindingDetailsRoot"));

/**
 * The admin binding details route: see BindingDetailsRoot.
 *
 * The read-only mirror of the same page (`/bindings/:bindingUUID`) stays on
 * lazy load for now — it asks for the same binding by the same param, so it
 * could take this loader as it stands, but nothing links to it from a list
 * that could preload on hover.
 */
export const bindingDetailsRoute: PreloadingRouteProps = {
  element: (
    <Suspense fallback={<PageSkeleton />}>
      <BindingDetailsRoute />
    </Suspense>
  ),
  loader: lazyRouteLoader({
    load: () =>
      import("./bindingDetailsPreload").then((module) =>
        module.createBindingDetailsLoader(),
      ),
    warm: () => import("./BindingDetailsRoot"),
  }),
  // The details page keeps its own query up to date while it is mounted (see
  // useRoutePreloadedQuery), so the loader only runs again when the URL names
  // a different binding — not for the `?view` the page writes when a tab is
  // switched, which it handles itself.
  shouldRevalidate: ({ currentParams, nextParams }) =>
    currentParams.bindingUUID !== nextParams.bindingUUID,
};
